import dayjs from "dayjs";
import { formatFieldName } from "./stringUtils";

/**
 * Utility functions for formatting filter values in ActiveFilters components
 */

/**
 * Helper function to format date values for better readability
 * @param value - The date string value to format
 * @returns Formatted date string
 */
export const formatDateValue = (value: string): string => {
	try {
		// Handle date ranges like "2024-01-01,2024-12-31"
		if (value.includes(",")) {
			const dates = value.split(",");
			if (dates.length === 2) {
				const startDate = dayjs(dates[0]).format("DD.MM.YYYY");
				const endDate = dayjs(dates[1]).format("DD.MM.YYYY");
				return `${startDate} - ${endDate}`;
			}
		}

		// Handle single date
		const date = dayjs(value);
		if (date.isValid()) {
			return date.format("DD.MM.YYYY");
		}
	} catch (_error) {
		// If parsing fails, return original value
	}

	return value;
};

/**
 * Helper function to format sorting values for better readability
 * @param value - The sorting string value to format
 * @returns Formatted sorting string
 */
export const formatSortingValue = (value: string): string => {
	try {
		// Parse the sorting JSON array
		const sortingArray = JSON.parse(decodeURIComponent(value));

		if (Array.isArray(sortingArray) && sortingArray.length > 0) {
			return sortingArray
				.map((sort) => {
					const field = sort.id || sort.field || "Unknown";
					const direction = sort.desc ? "Descending" : "Ascending";

					// Format field name to be more readable
					const readableField = formatFieldName(field);

					return `${readableField} (${direction})`;
				})
				.join(", ");
		}
	} catch (_error) {
		// If parsing fails, return a simplified version
		if (value.includes("desc")) {
			return "Date Starting (Descending)";
		}
	}

	return value;
};

/**
 * Helper function to format array-like values
 * @param value - The array-like string value to format
 * @returns Formatted array string
 */
export const formatArrayValue = (value: string): string => {
	try {
		// Handle JSON arrays
		if (value.startsWith("[") && value.endsWith("]")) {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) {
				return parsed.join(", ");
			}
		}

		// Handle comma-separated values
		if (value.includes(",")) {
			return value
				.split(",")
				.map((v) => v.trim())
				.join(", ");
		}
	} catch (_error) {
		// If parsing fails, return original value
	}

	return value;
};
